import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthenticatedTemplate, UnauthenticatedTemplate, useMsal } from '@azure/msal-react';
import { useTranslation } from 'react-i18next';
import Layout from './components/Layout';
import LoginPage        from './pages/LoginPage';
import OnboardingPage   from './pages/OnboardingPage';
import PatientsPage     from './pages/PatientsPage';
import AdminPage        from './pages/AdminPage';
import ConsultationPage from './pages/ConsultationPage';
import AnalysisPage     from './pages/AnalysisPage';
import FootAnalysisPage from './pages/FootAnalysisPage';
import GaitAnalysisPage from './pages/GaitAnalysisPage';
import ClinicalSummaryPage from './pages/ClinicalSummaryPage';
import SubscriptionPage from './pages/SubscriptionPage';
import PatientHistoryPage from './pages/PatientHistoryPage';

export const API = process.env.REACT_APP_API_URL || '';

function AuthenticatedApp() {
  const { accounts } = useMsal();
  const { i18n } = useTranslation();
  const practitionerId = (accounts[0]?.localAccountId || '').split(':')[0];
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [hasProfile,    setHasProfile]    = useState(false);

  useEffect(() => {
    if (!practitionerId) return;
    fetch(`${API}/api/practitioners/${practitionerId}`)
      .then(r => r.json())
      .then(d => {
        if (d.exists) {
          setHasProfile(true);
          if (d.language) {
            i18n.changeLanguage(d.language);
            localStorage.setItem('lang', d.language);
          }
        }
        setProfileLoaded(true);
      })
      .catch(() => setProfileLoaded(true));
  }, [practitionerId]);

  // Après un aller-retour externe (ex: paiement Stripe), la page se recharge
  // entièrement : MSAL doit rétablir la session et le profil praticien doit
  // être re-fetché avant de pouvoir afficher quoi que ce soit. Avant, on
  // affichait littéralement rien (return null) pendant ce court laps de
  // temps, ce qui donnait une fenêtre vide/blanche difficile à interpréter.
  if (!profileLoaded) {
    return (
      <div style={{
        height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'var(--bg)', color: 'var(--text-dim)', fontFamily: 'var(--font-mono)', fontSize: 13,
      }}>
        Chargement…
      </div>
    );
  }

  if (!hasProfile) {
    return (
      <OnboardingPage
        practitionerId={practitionerId}
        onComplete={(profile) => {
          i18n.changeLanguage(profile.language);
          localStorage.setItem('lang', profile.language);
          setHasProfile(true);
        }}
      />
    );
  }

  return (
    <Layout>
      <Routes>
        <Route path="/"                         element={<PatientsPage />} />
        <Route path="/patient/:id/admin"        element={<AdminPage />} />
        <Route path="/patient/:id/consultation" element={<ConsultationPage />} />
        <Route path="/analysis/:id"             element={<AnalysisPage />} />
        <Route path="/foot-analysis/:id"        element={<FootAnalysisPage />} />
        <Route path="/gait/:id"                 element={<GaitAnalysisPage />} />
        <Route path="/clinical-summary/:id"     element={<ClinicalSummaryPage />} />
        <Route path="/subscription"             element={<SubscriptionPage />} />
        <Route path="*"                         element={<Navigate to="/" replace />} />
        <Route path="/patient/:id/history"      element={<PatientHistoryPage />} />
      </Routes>
    </Layout>
  );
}

export default function App() {
  return (
    <>
      <UnauthenticatedTemplate>
        <LoginPage />
      </UnauthenticatedTemplate>
      <AuthenticatedTemplate>
        <AuthenticatedApp />
      </AuthenticatedTemplate>
    </>
  );
}
